import { StyleProvider } from "@ant-design/cssinjs";
import { useApolloClient } from "@apollo/client";
import { App, ConfigProvider, message } from "antd";
import type { Locale } from "antd/es/locale";
import type { MessageInstance } from "antd/es/message/interface";
import { startCase } from "lodash";
import {
  Fragment,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import { useCookies } from "react-cookie";
import { IntlProvider } from "react-intl";

import type { UserError } from "../../../lib/graphql";
import localeConfig, {
  type AvailableLocales,
} from "../../../lib/locale/config";
import { getThemeConfig, pageBackgroundColor } from "../../../lib/themeConfig";
import { DefaultCookieOptions, LOCALE_COOKIE, THEME_COOKIE } from "./cookies";

export type ThemeMode = "light" | "dark" | "auto";

interface LocaleContextType {
  currentLocale: AvailableLocales;
  setLocale(locale: AvailableLocales): void;
  antLocale: Locale;
  showError: MessageInstance["error"];
  showSuccess: MessageInstance["success"];
  showErrors(errors: Array<Pick<UserError, "message" | "path">>): void;
  themeMode: ThemeMode;
  setThemeMode(mode: ThemeMode): void;
  isDark: boolean;
}

export const LocaleContext = createContext<LocaleContextType>({
  currentLocale: "es",
  setLocale: () => {},
  antLocale: localeConfig("es"),
  showError: undefined as any,
  showSuccess: undefined as any,
  showErrors: undefined as any,
  themeMode: "light",
  setThemeMode: () => {},
  isDark: false,
});

function showErrors(errors: Array<Pick<UserError, "message" | "path">>) {
  if (!errors.length) return;

  message.error(
    <>
      {errors.map((error, i) => (
        <Fragment key={i}>
          {startCase(error.path)}: {error.message}
          <br />
        </Fragment>
      ))}
    </>
  );
}

export const LocaleProvider: FC<
  React.PropsWithChildren<{
    locale?: AvailableLocales;
    language?: string;
    timezone?: string;
  }>
> = ({ locale, language, timezone, children }) => {
  const [cookies, setCookie] = useCookies();
  const currentLocale = locale || cookies[LOCALE_COOKIE] || language;
  const currentLocaleConfig = localeConfig(currentLocale);
  const apolloClient = useApolloClient();
  const [messageApi, contextHolder] = message.useMessage();

  // Theme mode: defaults to light; a saved "auto" preference follows the OS.
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const savedThemeMode = cookies[THEME_COOKIE];
    if (["light", "dark", "auto"].includes(savedThemeMode)) {
      return savedThemeMode;
    }
    return "light";
  });
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    setSystemPrefersDark(media.matches);
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, []);

  const isDark =
    themeMode === "dark" || (themeMode === "auto" && systemPrefersDark);

  // Stamp the page-level background on `body` so overscroll bounces and any
  // uncovered area behind the antd layout stay on-theme. Has to be imperative
  // because antd v6 cssVar mode scopes `--ant-color-*` to its `.ant-app`
  // subtree — body is above that subtree.
  //
  // Also stamp `data-theme` on body so global stylesheets can target the
  // active theme for things antd doesn't reach — like @ant-design/plots HTML
  // overlays (tooltip, statistic, legend popover).
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.backgroundColor = pageBackgroundColor(isDark);
    document.body.dataset.theme = isDark ? "dark" : "light";
  }, [isDark]);

  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      setCookie(THEME_COOKIE, mode, DefaultCookieOptions);
      setThemeModeState(mode);
    },
    [setCookie]
  );

  const setLocale = useCallback(
    (locale: AvailableLocales) => {
      setCookie(LOCALE_COOKIE, locale, DefaultCookieOptions);
      apolloClient.resetStore();
    },
    [apolloClient, setCookie]
  );

  const contextValue = useMemo(
    () => ({
      currentLocale,
      setLocale,
      antLocale: currentLocaleConfig.ant,
      showError: messageApi.error,
      showSuccess: messageApi.success,
      showErrors,
      themeMode,
      setThemeMode,
      isDark,
    }),
    [
      currentLocale,
      setLocale,
      currentLocaleConfig.ant,
      messageApi.error,
      messageApi.success,
      themeMode,
      setThemeMode,
      isDark,
    ]
  );

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider
        locale={currentLocale === "es" ? "es-NI" : currentLocale}
        timeZone={timezone}
        messages={currentLocaleConfig.messages}
      >
        <StyleProvider hashPriority="low">
          <ConfigProvider
            locale={currentLocaleConfig.ant}
            tag={{
              variant: "outlined",
              // antd v6 dropped Tag's default trailing margin (see migration
              // guide). Re-introduce it globally so existing `<Tag>` lists
              // keep breathing room without sprinkling Flex/Space everywhere.
              styles: { root: { marginInlineEnd: 4 } },
            }}
            theme={getThemeConfig(isDark)}
          >
            <App>
              {contextHolder}

              {children}
            </App>
          </ConfigProvider>
        </StyleProvider>
      </IntlProvider>
    </LocaleContext.Provider>
  );
};
